//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import ShapesModel from '../models/shapes.model';

/**
 * @service ShapesService
 * @description Service for managing Shape definitions within the Material Management System (MMS).
 * Shapes define the geometric forms of items or infrastructure components, such as ponds (e.g., Circular, Rectangular, Square).
 *
 * This service provides the interface to retrieve these definitions from the backend API.
 * It is essential for:
 * 1. **Infrastructure Configuration**: Defining the shape of ponds during farm setup.
 * 2. **Inventory Management**: Specifying the form factor of certain materials if applicable.
 * 3. **Calculations**: Shape data might be used for volume or area calculations in other parts of the system.
 *
 * The service interacts with the `/mms/shapes/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Shapes.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all shapes are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<ShapesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/shapes/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ShapesService extends Service<ShapesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/shapes/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/shapes/');
  }

  /**
   * @method getAllShapes
   * @async
   * @description Fetch all Shape records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all shape definitions are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<ShapesModel>>} Promise resolving to an array of ShapesModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * shapesService.getAllShapes().then(shapes => {
   *   console.log('Defined Shapes:', shapes);
   * });
   */
  public getAllShapes() {
    return new Promise<Array<ShapesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ShapesModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
